import { useEffect, useMemo, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Autocomplete } from '@/components/Autocomplete'
import { Loader2 } from 'lucide-react'
import { useRefrigerantTypes, ModelSpecLookupResult } from '@/equipment/api'

const ASSET_TYPES = [
  { value: 'air_conditioner', label: 'Mini-Split Air Conditioner' },
  { value: 'split_air_conditioner', label: 'Split Air Conditioner' },
  { value: 'multi_split_air_conditioner', label: 'Multi-Split Air Conditioner' },
  { value: 'window_air_conditioner', label: 'Window Air Conditioner' },
  { value: 'mini_split_heat_pump', label: 'Mini-Split Heat Pump' },
  { value: 'split_heat_pump', label: 'Split Heat Pump' },
  { value: 'multi_split_heat_pump', label: 'Multi-Split Heat Pump' },
  { value: 'rooftop_unit', label: 'Packaged Rooftop Unit (RTU)' },
  { value: 'ptac', label: 'Packaged Terminal Air Conditioner (PTAC)' },
  { value: 'pthp', label: 'Packaged Terminal Heat Pump (PTHP)' },
  { value: 'wshp', label: 'WSHP' },
  { value: 'chiller', label: 'Chiller' },
  { value: 'boiler', label: 'Boiler' },
  { value: 'furnace', label: 'Furnace' },
  { value: 'hotwater', label: 'Hot Water Heater' },
  { value: 'hybrid_fuel_system', label: 'Hybrid Fuel System (HP + Gas Furnace)' },
  { value: 'cogen', label: 'Cogeneration System (Cogen)' },
  { value: 'dac', label: 'Direct Air Capture' },
  { value: 'ambient', label: 'Ambient' },
  { value: 'air_quality', label: 'Air Quality Monitor' },
  { value: 'other', label: 'Other' },
]

const REFRIGERANT_SUGGESTIONS = ['R410A', 'R134a', 'R454B']

export type ModelSpecs = {
  asset_type: string
  phase_count: number | null
  refrigerant_type: string | null
  nameplate_capacity_btu: number | null
}

type ModelSpecsDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  manufacturerName: string
  modelName: string
  isLoading: boolean
  lookupResult: ModelSpecLookupResult | undefined
  lookupFailed: boolean
  onConfirm: (specs: ModelSpecs) => void
}

export function ModelSpecsDialog({
  open,
  onOpenChange,
  manufacturerName,
  modelName,
  isLoading,
  lookupResult,
  lookupFailed,
  onConfirm,
}: ModelSpecsDialogProps) {
  const { data: refrigerantTypes } = useRefrigerantTypes()

  const [specs, setSpecs] = useState<ModelSpecs>({
    asset_type: 'air_conditioner',
    phase_count: null,
    refrigerant_type: null,
    nameplate_capacity_btu: null,
  })

  const [hasInitialized, setHasInitialized] = useState(false)

  // Initialize specs from lookup result when it arrives
  useEffect(() => {
    if (lookupResult && !hasInitialized) {
      setSpecs({
        asset_type: lookupResult.asset_type || 'air_conditioner',
        phase_count: lookupResult.phase_count,
        refrigerant_type: lookupResult.refrigerant_type,
        nameplate_capacity_btu: lookupResult.nameplate_capacity_btu,
      })
      setHasInitialized(true)
    }
  }, [lookupResult, hasInitialized])

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setHasInitialized(false)
      setSpecs({
        asset_type: 'air_conditioner',
        phase_count: null,
        refrigerant_type: null,
        nameplate_capacity_btu: null,
      })
    }
  }, [open])

  // Build refrigerant items for autocomplete
  const refrigerants = useMemo(() => {
    if (!refrigerantTypes) return []
    return refrigerantTypes.map((type, index) => ({
      id: index,
      name: type.label,
      value: type.value,
    }))
  }, [refrigerantTypes])

  const selectedRefrigerantId = useMemo(() => {
    if (!specs.refrigerant_type) return null
    const index = refrigerantTypes?.findIndex(type => type.value === specs.refrigerant_type)
    return index !== undefined && index >= 0 ? index : null
  }, [specs.refrigerant_type, refrigerantTypes])

  const refrigerantSuggestions = useMemo(() => {
    if (!refrigerants.length) return []
    return [{
      heading: 'Suggestions',
      items: refrigerants.filter(({ name }) => REFRIGERANT_SUGGESTIONS.includes(name)),
    }]
  }, [refrigerants])

  const handleSelectRefrigerant = (id: number, _name: string) => {
    const refrigerantType = refrigerantTypes?.[id]
    if (refrigerantType) {
      setSpecs(prev => ({ ...prev, refrigerant_type: refrigerantType.value }))
    }
  }

  const handleClearRefrigerant = () => {
    setSpecs(prev => ({ ...prev, refrigerant_type: null }))
  }

  const handleConfirm = () => {
    onConfirm(specs)
  }

  const handleCancel = () => {
    onOpenChange(false)
  }

  const getTitle = () => {
    if (isLoading) return 'Looking up model details'
    if (lookupFailed) return 'Enter model specifications'
    return 'Confirm model specifications'
  }

  const getDescription = () => {
    if (isLoading) return "We haven't seen this model before, give us a second to find its specs."
    if (lookupFailed) return `We couldn't find specs for ${manufacturerName} ${modelName}. Please enter them manually.`
    return `Review and edit the specifications for ${manufacturerName} ${modelName}.`
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{getTitle()}</DialogTitle>
          <DialogDescription>{getDescription()}</DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : (
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="asset_type">Equipment Type</Label>
              <Select
                value={specs.asset_type}
                onValueChange={(value) => setSpecs(prev => ({ ...prev, asset_type: value }))}
              >
                <SelectTrigger id="asset_type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ASSET_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="phase_count">Phase Count</Label>
              <Select
                value={specs.phase_count?.toString() || ''}
                onValueChange={(value) => setSpecs(prev => ({
                  ...prev,
                  phase_count: value ? parseInt(value, 10) : null
                }))}
              >
                <SelectTrigger id="phase_count">
                  <SelectValue placeholder="Select phase count" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">Single Phase</SelectItem>
                  <SelectItem value="3">Three Phase</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="refrigerant_type">Refrigerant Type</Label>
              <Autocomplete
                id="refrigerant_type"
                items={refrigerants}
                itemsHeading="All refrigerants"
                selectedId={selectedRefrigerantId}
                selectedName={specs.refrigerant_type || ''}
                onSelect={handleSelectRefrigerant}
                onClear={handleClearRefrigerant}
                placeholder="Search refrigerant type..."
                emptyMessage="No refrigerant types found"
                suggestions={refrigerantSuggestions}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="nameplate_capacity">Nameplate Capacity (BTU/hr)</Label>
              <Input
                id="nameplate_capacity"
                type="number"
                placeholder="e.g., 36000"
                value={specs.nameplate_capacity_btu ?? ''}
                onChange={(e) => setSpecs(prev => ({
                  ...prev,
                  nameplate_capacity_btu: e.target.value ? parseInt(e.target.value, 10) : null
                }))}
              />
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={isLoading}>
            Confirm
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
